<template>
  <SfDropdown
    :is-open="isScopedDropdownOpen"
    :dropdown-id="dropdownId"
    :is-disabled="isDisabled"
    :tooltip="tooltip"
    :constrain-menu-to-trigger-width="constrainMenuToTriggerWidth"
    :select-class-names="selectClassNames"
    :menu-class-names="menuClassNames"
    @dropdown-clicked="handleToggleDropdown"
    @dropdown-closed="closeDropdown"
  >
    <template #trigger>
      <div class="tw-flex tw-items-center">
        <div
          ref="triggerEl"
          class="
            tw-flex-1
            tw-min-w-0
            tw-whitespace-nowrap
            tw-text-ellipsis
            tw-overflow-hidden
          "
          :class="triggerDisplayClass"
        >
          {{ triggerDisplayValue }}
        </div>
        <span
          v-if="selectedValue && isClearable && !isDisabled"
          class="
            fa fa-remove
            tw-cursor-pointer
            sf-search__icon sf-search__icon--right
            tw-ml-2
          "
          data-testid="sf-search-clear"
          @click.prevent.stop="clearSelected"
        />
      </div>
    </template>

    <template #menu>
      <div class="tw-p-2 tw-overflow-hidden">
        <div class="tw-mb-2">
          <SfSearch
            v-model="searchValue"
            :show-search-icon="true"
            :show-clear-icon="true"
            :show-result-navigation="false"
            :placeholder="searchPlaceholder"
            :bottom-margin="true"
            :focused-at="focusedAt"
            @clear="clearSearch"
          />
        </div>
        <div class="tw-overflow-y-auto tw-max-h-[350px]">
          <SfDropdownItem
            v-for="item in filteredOptions"
            :key="String(item.value)"
            :has-border="false"
            :is-rounded="true"
            :is-wrapped="constrainMenuToTriggerWidth"
            :has-checkmark="isSelected(item.value)"
            :class="{
              'tw-leading-3': !constrainMenuToTriggerWidth,
              'tw-font-medium tw-bg-sf-primary-subtle': isSelected(item.value),
            }"
            @click="handleItemClick(item.value)"
          >
            <span
              v-if="item.icon"
              :class="`fa-${item.icon} tw-text-${item.iconColor}`"
              class="fa tw-mr-3"
            />
            <span>{{ item.label }}</span>
          </SfDropdownItem>
        </div>
      </div>
    </template>
  </SfDropdown>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useVModel } from '@vueuse/core'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { useSearchable } from '@app/shared/vue/composables/use-searchable'
import useDynamicTooltip from '@app/shared/vue/composables/use-dynamic-tooltip'
import { useDropdown } from '@app/shared/pinia/dropdown'
import SfDropdown from '@app/shared/vue/components/sf-dropdown/sf-dropdown.vue'
import SfDropdownItem from '@app/shared/vue/components/sf-dropdown/sf-dropdown-item.vue'
import SfSearch from '@app/shared/vue/components/sf-search/search-input.vue'

interface Option {
  label: string
  value: string | number | null
  icon?: string
  iconColor?: string
}

interface SearchableDropdownProps {
  modelValue: string | number | null
  options: Option[]
  dropdownId?: string
  isDisabled?: boolean
  isClearable?: boolean
  // Cap the menu at the trigger width (forwarded to SfDropdown). Off by default
  // so existing consumers keep sizing the menu to their content.
  constrainMenuToTriggerWidth?: boolean
  // Extra classes for the menu, forwarded to SfDropdown.
  menuClassNames?: string[]
  hasError?: boolean
  placeholder?: string | null
  searchPlaceholder?: string | null
  // Label to show on the trigger when the current value matches no option —
  // e.g. a partner template the firm hasn't imported, whose name is known but
  // isn't (and shouldn't be) part of the fetched option list. Falls back to the
  // placeholder when unset.
  selectedLabel?: string | null
}

const props = withDefaults(defineProps<SearchableDropdownProps>(), {
  dropdownId: 'searchableDropdown',
  isClearable: true,
  menuClassNames: () => [],
  hasError: false,
  placeholder: null,
  searchPlaceholder: null,
  selectedLabel: null,
})

const selectClassNames = computed(() =>
  props.hasError ? ['sf-form-warning'] : []
)

const I18n = useI18n()
const dropdownStore = useDropdown()

const emit =
  defineEmits<{
    'update:modelValue': [value: string | number | null]
    opened: []
    closed: []
  }>()

const selectedValue = useVModel(props, 'modelValue', emit, {
  deep: true,
  passive: true,
})
const { currentDropdownId } = storeToRefs(dropdownStore)
const focusedAt = ref<number | undefined>(undefined)

const selectedOption = computed(
  () =>
    props.options.find((option) => option.value === selectedValue.value) ?? null
)

const triggerDisplayValue = computed(() => {
  if (selectedOption.value) {
    return selectedOption.value.label
  } else if (props.selectedLabel) {
    return props.selectedLabel
  } else {
    return props.placeholder || I18n.t('common.select')
  }
})

// Surface the full label as a tooltip only when the (single-line) trigger text
// is actually cut off.
const triggerEl = ref<HTMLElement | null>(null)
const tooltip = useDynamicTooltip(triggerEl, triggerDisplayValue)

onBeforeUnmount(() => {
  // Don't leave this dropdown marked open in the shared store once it's gone,
  // or a later dropdown reusing the same id would render pre-opened.
  if (currentDropdownId.value === props.dropdownId) {
    dropdownStore.closeDropdown()
  }
})

const flattenedOptions = computed(() => {
  return props.options.flat()
})

const { searchValue, results: filteredOptions } = useSearchable<Option, Option>(
  flattenedOptions,
  {
    prepareItem: (item) => item,
    fuzzySortOptions: { keys: ['label'] },
  }
)

const isSelected = computed(() => {
  return (value: string | number | null) => selectedValue.value === value
})

const isScopedDropdownOpen = computed(() => {
  return currentDropdownId.value === props.dropdownId
})

const triggerDisplayClass = computed(() => {
  // A selectedLabel fallback reads as a real selection, not a placeholder.
  const hasSelection =
    Boolean(selectedOption.value) || Boolean(props.selectedLabel)
  return {
    'tw-text-sf-gray-darker': hasSelection,
    'tw-text-sf-gray-light': !hasSelection,
    'tw-text-sf-gray-lighter': props.isDisabled,
    'tw-italic': !hasSelection,
  }
})

function handleToggleDropdown() {
  if (props.isDisabled) return

  if (isScopedDropdownOpen.value) {
    emit('closed')
  } else {
    emit('opened')
  }

  dropdownStore.toggleDropdown(props.dropdownId)
  focusSearch()
}

function closeDropdown() {
  dropdownStore.closeDropdown()
  emit('closed')
}

function clearSearch(): void {
  searchValue.value = ''
}

function clearSelected(): void {
  if (props.isDisabled) return

  selectedValue.value = null
}

function focusSearch(): void {
  focusedAt.value = Date.now()
}

function handleItemClick(value: string | number | null): void {
  selectedValue.value = value
  closeDropdown()
}
</script>
